import { Link, createFileRoute } from '@tanstack/react-router'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Content as CardContent,
  Header as CardHeader,
  Heading as CardTitle,
  InlineAlert,
  ProgressBar,
  StatusLight,
  TextArea,
  TextField as Input,
} from '@react-spectrum/s2'
import { useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  Search,
} from 'lucide-react'
import {
  getAdminRagRevisionWorkflow,
  retryAdminRagIngest,
  searchAdminRagRevision,
  submitAdminRagChunkReviewAndResume,
  submitAdminRagPageReviewAndStartIngest,
} from '@rpc/admin.rpc'
import { fullWidthStyles } from '@/styles/s2-macros'

export const Route = createFileRoute(
  '/admin/documents/$documentId/revisions/$revisionId',
)({
  loader: async ({ params }) =>
    getAdminRagRevisionWorkflow({
      data: { revisionId: params.revisionId },
    }),
  component: RagRevisionWorkflowPage,
})

type WorkflowData = Awaited<ReturnType<typeof getAdminRagRevisionWorkflow>>
type CandidateSearchResponse = Awaited<
  ReturnType<typeof searchAdminRagRevision>
>
type WorkflowRunState = {
  kind:
    | 'not_started'
    | 'queued'
    | 'running'
    | 'awaiting_review'
    | 'blocked'
    | 'failed'
    | 'completed'
  label: string
  summary: string
  variant: 'neutral' | 'informative' | 'notice' | 'negative' | 'positive'
}

const ACTIVE_JOB_STATUSES = new Set(['queued', 'running', 'awaiting_review'])
const STAGES = [
  'structure_scan',
  'page_skip_finalize',
  'page_render_upload',
  'page_markdown_extract',
  'chunk_plan_openai',
  'embed_text',
  'embed_image',
  'index_finalize',
]

function RagRevisionWorkflowPage() {
  const { documentId, revisionId } = Route.useParams()
  const loaderData = Route.useLoaderData()
  const [workflow, setWorkflow] = useState<WorkflowData>(loaderData)
  const [skipPagesSelection, setSkipPagesSelection] = useState<{
    revisionId: string
    value: string
  } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [chunkReason, setChunkReason] = useState(
    'Reviewed in admin UI; safe to ignore uncovered units.',
  )
  const [manualHeading, setManualHeading] = useState('')
  const [manualSectionPath, setManualSectionPath] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedChunkUnitIds, setSelectedChunkUnitIds] = useState<
    Record<string, Array<string> | undefined>
  >({})
  const [searchResult, setSearchResult] =
    useState<CandidateSearchResponse | null>(null)

  const metadata = getMetadata(workflow.revision)
  const suggestedSkipPages = useMemo(
    () => getSuggestedSkipPages(metadata, workflow.structureReview),
    [metadata, workflow.structureReview],
  )
  const skipPages =
    skipPagesSelection?.revisionId === revisionId
      ? skipPagesSelection.value
      : suggestedSkipPages
  const setSkipPages = (value: string) => {
    setSkipPagesSelection({ revisionId, value })
  }
  const latestJob = getLatestJob(workflow.status)
  const revisionStatus = String(getField(workflow.revision, 'status') || '')
  const latestJobStatus = String(
    getField(latestJob, 'status') || (latestJob ? 'queued' : ''),
  )
  const currentStage = String(getField(latestJob, 'current_stage') || '')
  const pageReviewReady = hasPageReviewResult(
    metadata,
    workflow.structureReview,
  )
  const pageReviewScanPending =
    !latestJob &&
    revisionStatus === 'draft' &&
    !pageReviewReady &&
    wasPageReviewRequested(metadata)
  const needsPageReview =
    (latestJobStatus === 'awaiting_review' &&
      currentStage === 'page_skip_finalize') ||
    (!latestJob && revisionStatus === 'draft' && pageReviewReady)
  const runState = getWorkflowRunState(
    latestJob,
    workflow.events,
    revisionStatus,
    { pageReviewReady: needsPageReview, pageReviewScanPending },
  )
  const isActive = ACTIVE_JOB_STATUSES.has(latestJobStatus)
  const isProcessing =
    latestJobStatus === 'queued' || latestJobStatus === 'running'
  const isFailed = runState.kind === 'failed'
  const isBlocked = runState.kind === 'blocked'
  const hasRunProblem = isFailed || isBlocked
  const isComplete = revisionStatus === 'completed'
  const needsChunkReview =
    latestJobStatus === 'awaiting_review' &&
    currentStage === 'chunk_plan_openai' &&
    workflow.chunkReview.length > 0
  const shouldMonitorIngest =
    !needsPageReview && !needsChunkReview && !isComplete
  const progressCounts = getProgressCounts(workflow)
  const readinessIssues = getReadinessIssues(progressCounts, isComplete)
  const pageReviewSummary = getPageReviewSummary(
    workflow.events,
    progressCounts,
    needsPageReview,
    revisionStatus,
    latestJob,
  )

  useEffect(() => {
    setWorkflow(loaderData)
  }, [loaderData])

  useEffect(() => {
    setSelectedChunkUnitIds((current) => {
      const next: Record<string, Array<string>> = {}
      for (const candidate of workflow.chunkReview) {
        next[candidate.id] =
          current[candidate.id] ?? getCandidateUnitIds(candidate)
      }
      return next
    })
  }, [workflow.chunkReview])

  useEffect(() => {
    if (!isActive && !pageReviewScanPending) return
    const id = window.setInterval(() => {
      void refreshWorkflow(false)
    }, 5000)
    return () => window.clearInterval(id)
  }, [isActive, pageReviewScanPending, revisionId])

  const refreshWorkflow = async (showSpinner = true) => {
    if (showSpinner) setIsRefreshing(true)
    try {
      const next = await getAdminRagRevisionWorkflow({
        data: { revisionId },
      })
      setWorkflow(next)
      setActionError(null)
    } catch (error: any) {
      setActionError(error?.message || 'Failed to refresh revision state.')
    } finally {
      if (showSpinner) setIsRefreshing(false)
    }
  }

  const startIngest = async () => {
    setIsSubmitting(true)
    setActionError(null)
    try {
      await submitAdminRagPageReviewAndStartIngest({
        data: { revisionId, skipPages, restart: isFailed },
      })
      await refreshWorkflow(false)
    } catch (error: any) {
      setActionError(error?.message || 'Failed to start ingestion.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const retryIngest = async () => {
    setIsSubmitting(true)
    setActionError(null)
    try {
      await retryAdminRagIngest({ data: { revisionId } })
      await refreshWorkflow(false)
    } catch (error: any) {
      setActionError(error?.message || 'Failed to retry ingestion.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const ignoreChunkReviewUnits = async () => {
    const decisions = workflow.chunkReview
      .map((candidate) => ({
        candidateId: candidate.id,
        unitIds: Array.from(new Set(selectedChunkUnitIds[candidate.id])),
        reason: chunkReason.trim(),
      }))
      .filter(
        (decision) => decision.unitIds.length > 0 && decision.reason.length > 0,
      )
    if (decisions.length === 0) {
      setActionError('Chunk review needs at least one unit and a reason.')
      return
    }

    setIsSubmitting(true)
    setActionError(null)
    try {
      await submitAdminRagChunkReviewAndResume({
        data: { revisionId, ignoredUnits: decisions },
      })
      await refreshWorkflow(false)
    } catch (error: any) {
      setActionError(error?.message || 'Failed to resume ingestion.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const createManualChunk = async () => {
    const candidate = workflow.chunkReview[0]!
    const unitIds = Array.from(new Set(selectedChunkUnitIds[candidate.id]))
    if (unitIds.length === 0 || !manualHeading.trim()) {
      setActionError('Manual chunk review needs a heading and source units.')
      return
    }

    setIsSubmitting(true)
    setActionError(null)
    try {
      await submitAdminRagChunkReviewAndResume({
        data: {
          revisionId,
          manualChunks: [
            {
              candidateId: candidate.id,
              unitIds,
              heading: manualHeading.trim(),
              sectionPath: manualSectionPath.trim() || null,
            },
          ],
        },
      })
      await refreshWorkflow(false)
    } catch (error: any) {
      setActionError(error?.message || 'Failed to create manual chunk.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const runScopedSearch = async () => {
    setIsSubmitting(true)
    setActionError(null)
    try {
      const result = await searchAdminRagRevision({
        data: { revisionId, query: searchQuery },
      })
      setSearchResult(result)
    } catch (error: any) {
      setActionError(error?.message || 'Revision search failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link
            to="/admin"
            className="mb-3 inline-flex items-center text-sm text-neutral-500 transition-colors hover:text-neutral-900 dark:hover:text-neutral-300"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-neutral-950 dark:text-neutral-50">
            {String(getField(workflow.document, 'title') || 'RAG document')}
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Revision {revisionId} for document {documentId}
          </p>
        </div>
        <Button
          variant="secondary"
          fillStyle="outline"
          onPress={() => void refreshWorkflow(true)}
          isDisabled={isRefreshing}
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCcw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      {actionError && (
        <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{actionError}</span>
        </div>
      )}

      {hasRunProblem && <WorkflowStatusAlert runState={runState} />}

      <Card styles={fullWidthStyles}>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-4">
            <StepBadge label="Upload" isDone />
            <StepBadge
              label="Page review"
              isActive={needsPageReview || pageReviewScanPending}
              isDone={!needsPageReview && revisionStatus !== 'draft'}
            />
            <StepBadge
              label="Ingest"
              isActive={isActive && !hasRunProblem && !needsPageReview}
              isDone={isComplete}
              isError={hasRunProblem}
            />
            <StepBadge label="Verify" isActive={isComplete} />
          </div>
        </CardContent>
      </Card>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6">
          {isComplete && (
            <VerificationCard
              isComplete={isComplete}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchResult={searchResult}
              isSubmitting={isSubmitting}
              onSearch={runScopedSearch}
            />
          )}

          {shouldMonitorIngest && (
            <ProgressCard
              latestJob={latestJob}
              events={workflow.events}
              currentStage={currentStage}
              runState={runState}
              isSubmitting={isSubmitting}
              onRetry={retryIngest}
            />
          )}

          {needsPageReview ? (
            <PageReviewCard
              skipPages={skipPages}
              setSkipPages={setSkipPages}
              suggestedSkipPages={suggestedSkipPages}
              candidates={workflow.structureReview}
              isDisabled={isSubmitting || isProcessing}
              onStart={startIngest}
            />
          ) : (
            <StageSummaryCard
              title="Page review"
              status={pageReviewSummary.status}
              variant={pageReviewSummary.variant}
              description={pageReviewSummary.description}
              details={pageReviewSummary.details}
            />
          )}

          {needsChunkReview && (
            <ChunkReviewCard
              candidates={workflow.chunkReview}
              chunkReason={chunkReason}
              setChunkReason={setChunkReason}
              manualHeading={manualHeading}
              setManualHeading={setManualHeading}
              manualSectionPath={manualSectionPath}
              setManualSectionPath={setManualSectionPath}
              selectedUnitIds={selectedChunkUnitIds}
              onToggleUnit={(candidateId, unitId, isSelected) =>
                setSelectedChunkUnitIds((current) => {
                  const values = new Set(current[candidateId])
                  if (isSelected) values.add(unitId)
                  else values.delete(unitId)
                  return { ...current, [candidateId]: [...values] }
                })
              }
              isSubmitting={isSubmitting}
              onIgnore={ignoreChunkReviewUnits}
              onManualChunk={createManualChunk}
            />
          )}

          {!shouldMonitorIngest && !needsPageReview && (
            <ProgressCard
              latestJob={latestJob}
              events={workflow.events}
              currentStage={currentStage}
              runState={runState}
              isSubmitting={isSubmitting}
              onRetry={retryIngest}
            />
          )}

          {!isComplete && (
            <StageSummaryCard
              title="Verify search"
              status="Locked"
              variant="neutral"
              description="Verification search becomes available after page extraction, chunking, and embeddings complete."
              details="This prevents a test search from running against a partial index."
            />
          )}
        </div>

        <div className="self-start lg:sticky lg:top-24">
          <IndexSummaryCard
            revisionStatus={revisionStatus}
            latestJobStatus={latestJobStatus}
            runState={runState}
            currentStage={currentStage}
            progressCounts={progressCounts}
            readinessIssues={readinessIssues}
          />
        </div>
      </div>
    </div>
  )
}

function PageReviewCard({
  skipPages,
  setSkipPages,
  suggestedSkipPages,
  candidates,
  isDisabled,
  onStart,
}: {
  skipPages: string
  setSkipPages: (value: string) => void
  suggestedSkipPages: string
  candidates: WorkflowData['structureReview']
  isDisabled: boolean
  onStart: () => void
}) {
  return (
    <Card styles={fullWidthStyles}>
      <CardHeader>
        <CardTitle>Page review</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Confirm front matter, contents, indexes, appendices, or blank pages
            that should not be chunked into chatbot evidence.
          </p>
          <div className="space-y-2">
            <label htmlFor="skip-pages">Pages to skip</label>
            <Input
              id="skip-pages"
              aria-label="Pages to skip"
              value={skipPages}
              onChange={setSkipPages}
              placeholder="e.g. 1-9,552-581"
              isDisabled={isDisabled}
            />
            <p className="text-xs text-neutral-500">
              Suggested: {suggestedSkipPages || 'No suggested skips yet.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              fillStyle="outline"
              onPress={() => setSkipPages(suggestedSkipPages)}
              isDisabled={isDisabled || !suggestedSkipPages}
            >
              Use suggested
            </Button>
            <Button
              variant="secondary"
              fillStyle="outline"
              onPress={() => setSkipPages('')}
              isDisabled={isDisabled}
            >
              Skip none
            </Button>
            <Button onPress={onStart} isDisabled={isDisabled}>
              Save and start ingestion
            </Button>
          </div>
          {candidates.length > 0 && (
            <div className="rounded border border-neutral-200 dark:border-neutral-800">
              <div className="border-b border-neutral-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-neutral-800">
                Review candidates
              </div>
              <div className="max-h-72 divide-y divide-neutral-100 overflow-y-auto dark:divide-neutral-800">
                {candidates.slice(0, 20).map((candidate, index) => (
                  <div
                    key={`${candidate.page_number}-${index}`}
                    className="p-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">
                        Page {candidate.page_number}
                      </span>
                      <Badge variant="neutral" fillStyle="outline">
                        {candidate.predicted_class || 'candidate'}
                      </Badge>
                    </div>
                    {candidate.rationale?.length ? (
                      <p className="mt-1 text-xs text-neutral-500">
                        {candidate.rationale.join(' ')}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function ChunkReviewCard({
  candidates,
  chunkReason,
  setChunkReason,
  manualHeading,
  setManualHeading,
  manualSectionPath,
  setManualSectionPath,
  selectedUnitIds,
  onToggleUnit,
  isSubmitting,
  onIgnore,
  onManualChunk,
}: {
  candidates: WorkflowData['chunkReview']
  chunkReason: string
  setChunkReason: (value: string) => void
  manualHeading: string
  setManualHeading: (value: string) => void
  manualSectionPath: string
  setManualSectionPath: (value: string) => void
  selectedUnitIds: Record<string, Array<string> | undefined>
  onToggleUnit: (
    candidateId: string,
    unitId: string,
    isSelected: boolean,
  ) => void
  isSubmitting: boolean
  onIgnore: () => void
  onManualChunk: () => void
}) {
  return (
    <Card styles={fullWidthStyles}>
      <CardHeader>
        <CardTitle>Chunk review required</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Chunk planning paused because the extractor found units that need an
            admin decision.
          </p>
          <div className="space-y-3">
            {candidates.map((candidate) => (
              <div
                key={candidate.id}
                className="rounded border border-neutral-200 p-3 dark:border-neutral-800"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="negative" fillStyle="outline">
                    {candidate.issue_kind || 'review'}
                  </Badge>
                  <span className="text-xs text-neutral-500">
                    Pages {candidate.window_start_page || '?'}-
                    {candidate.window_end_page || '?'}
                  </span>
                </div>
                <p className="mt-2 text-sm">{candidate.error_message}</p>
                <p className="mt-2 text-xs text-neutral-500">
                  {getCandidateUnitIds(candidate).length} unit(s) need review.
                </p>
                <div className="mt-3 space-y-2">
                  {getCandidateUnitIds(candidate).map((unitId) => (
                    <div
                      key={unitId}
                      className="rounded border border-neutral-100 bg-neutral-50 p-2 dark:border-neutral-800 dark:bg-neutral-900/50"
                    >
                      <Checkbox
                        isSelected={
                          selectedUnitIds[candidate.id]?.includes(unitId) ??
                          false
                        }
                        onChange={(isSelected) =>
                          onToggleUnit(candidate.id, unitId, isSelected)
                        }
                        isDisabled={isSubmitting}
                      >
                        {getUnitLabel(candidate, unitId)}
                      </Checkbox>
                      {getUnitPreview(candidate, unitId) ? (
                        <p className="mt-1 line-clamp-3 pl-7 text-xs text-neutral-500">
                          {getUnitPreview(candidate, unitId)}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <TextArea
            label="Ignore reason"
            value={chunkReason}
            onChange={setChunkReason}
            isDisabled={isSubmitting}
          />
          <Button onPress={onIgnore} isDisabled={isSubmitting}>
            Ignore listed units and resume
          </Button>
          <div className="grid gap-3 border-t border-neutral-100 pt-4 dark:border-neutral-800 sm:grid-cols-2">
            <Input
              aria-label="Manual chunk heading"
              placeholder="Manual chunk heading"
              value={manualHeading}
              onChange={setManualHeading}
              isDisabled={isSubmitting}
            />
            <Input
              aria-label="Manual chunk section path"
              placeholder="Optional section path"
              value={manualSectionPath}
              onChange={setManualSectionPath}
              isDisabled={isSubmitting}
            />
            <div className="sm:col-span-2">
              <Button
                variant="secondary"
                fillStyle="outline"
                onPress={onManualChunk}
                isDisabled={isSubmitting || !manualHeading.trim()}
              >
                Create manual chunk and resume
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function WorkflowStatusAlert({ runState }: { runState: WorkflowRunState }) {
  const variant = runState.kind === 'blocked' ? 'negative' : runState.variant
  return (
    <InlineAlert variant={variant} fillStyle="subtleFill">
      <CardTitle>{runState.label}</CardTitle>
      <CardContent>{runState.summary}</CardContent>
    </InlineAlert>
  )
}

function StageSummaryCard({
  title,
  status,
  variant,
  description,
  details,
}: {
  title: string
  status: string
  variant: WorkflowRunState['variant']
  description: string
  details?: string
}) {
  return (
    <Card styles={fullWidthStyles}>
      <CardContent>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CardTitle>{title}</CardTitle>
              {variant === 'positive' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : null}
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {description}
            </p>
            {details ? (
              <p className="text-xs text-neutral-500">{details}</p>
            ) : null}
          </div>
          <StatusLight variant={variant}>{status}</StatusLight>
        </div>
      </CardContent>
    </Card>
  )
}

function IndexSummaryCard({
  revisionStatus,
  latestJobStatus,
  runState,
  currentStage,
  progressCounts,
  readinessIssues,
}: {
  revisionStatus: string
  latestJobStatus: string
  runState: WorkflowRunState
  currentStage: string
  progressCounts: ReturnType<typeof getProgressCounts>
  readinessIssues: Array<string>
}) {
  return (
    <Card styles={fullWidthStyles}>
      <CardHeader>
        <CardTitle>Index summary</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-3 text-sm">
          <SummaryRow label="Revision status" value={revisionStatus || '-'} />
          <SummaryRow label="Latest job" value={latestJobStatus || '-'} />
          <SummaryRow label="Workflow state" value={runState.label} />
          <SummaryRow label="Current stage" value={currentStage || '-'} />
          <SummaryRow label="Pages" value={String(progressCounts.pages)} />
          <SummaryRow
            label="Skipped pages"
            value={String(progressCounts.skippedPages)}
          />
          <SummaryRow label="Chunks" value={String(progressCounts.chunks)} />
          <SummaryRow
            label="Text embeddings"
            value={String(progressCounts.textEmbeddings)}
          />
          <SummaryRow
            label="Image embeddings"
            value={String(progressCounts.imageEmbeddings)}
          />
        </dl>
        {readinessIssues.length > 0 && (
          <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
            <div className="font-semibold">Readiness checks</div>
            <ul className="mt-2 list-disc space-y-1 pl-4">
              {readinessIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ProgressCard({
  latestJob,
  events,
  currentStage,
  runState,
  isSubmitting,
  onRetry,
}: {
  latestJob: Record<string, unknown> | null
  events: Array<Record<string, unknown>>
  currentStage: string
  runState: WorkflowRunState
  isSubmitting: boolean
  onRetry: () => void
}) {
  const progressValue = getStageProgressValue(currentStage)
  const latestError = getLatestError(latestJob, events)
  const isFailed = runState.kind === 'failed'
  const isBlocked = runState.kind === 'blocked'
  const isQueued = runState.kind === 'queued'
  return (
    <Card styles={fullWidthStyles}>
      <CardHeader>
        <CardTitle>Ingestion progress</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          <div className="space-y-3">
            <StatusLight variant={runState.variant} role="status">
              {runState.label}
            </StatusLight>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {runState.summary}
            </p>
            <ProgressBar
              label="Ingest stage progress"
              value={isQueued ? undefined : progressValue}
              isIndeterminate={isQueued}
              valueLabel={
                isQueued
                  ? 'Waiting'
                  : isBlocked
                    ? 'Blocked'
                    : `${Math.round(progressValue)}%`
              }
            />
          </div>
          {latestError && (
            <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              <AlertCircle className="mt-0.5 h-4 w-4" />
              <span>
                <strong>Latest problem:</strong> {latestError}
              </span>
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {STAGES.map((stage) => (
              <div
                key={stage}
                className={`rounded border px-3 py-2 text-xs ${
                  currentStage === stage
                    ? 'border-neutral-900 bg-neutral-950 text-white dark:border-neutral-100 dark:bg-neutral-50 dark:text-neutral-950'
                    : 'border-neutral-200 text-neutral-600 dark:border-neutral-800 dark:text-neutral-400'
                }`}
              >
                {stage.replace(/_/g, ' ')}
              </div>
            ))}
          </div>
          {isFailed && (
            <Button onPress={onRetry} isDisabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCcw className="h-4 w-4" />
              )}
              Retry ingestion
            </Button>
          )}
          <div className="rounded border border-neutral-200 dark:border-neutral-800">
            <div className="border-b border-neutral-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-neutral-800">
              Recent events
            </div>
            <div className="max-h-80 divide-y divide-neutral-100 overflow-y-auto dark:divide-neutral-800">
              {events.length === 0 ? (
                <p className="p-3 text-sm text-neutral-500">
                  No events recorded yet.
                </p>
              ) : (
                events.slice(0, 30).map((event, index) => {
                  const severity = getEventSeverity(event)
                  return (
                    <div
                      key={index}
                      className={`p-3 text-sm ${
                        severity === 'negative'
                          ? 'bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-300'
                          : severity === 'notice'
                            ? 'bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-300'
                            : ''
                      }`}
                    >
                      <div className="font-medium">
                        {String(getField(event, 'stage') || 'event')}
                      </div>
                      <div
                        className={`text-xs ${
                          severity === 'neutral'
                            ? 'text-neutral-500'
                            : 'text-current opacity-80'
                        }`}
                      >
                        {String(getField(event, 'message') || '')}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
          {!latestJob && (
            <p className="text-sm text-neutral-500">No job has started yet.</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function VerificationCard({
  isComplete,
  searchQuery,
  setSearchQuery,
  searchResult,
  isSubmitting,
  onSearch,
}: {
  isComplete: boolean
  searchQuery: string
  setSearchQuery: (value: string) => void
  searchResult: CandidateSearchResponse | null
  isSubmitting: boolean
  onSearch: () => void
}) {
  return (
    <Card styles={fullWidthStyles}>
      <CardHeader>
        <CardTitle>Verify search</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Run a search scoped to this revision before relying on the document
            in normal chat.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              aria-label="Revision search query"
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="e.g. fire doorset"
              isDisabled={!isComplete || isSubmitting}
            />
            <Button
              onPress={onSearch}
              isDisabled={!isComplete || isSubmitting || !searchQuery.trim()}
            >
              <Search className="h-4 w-4" />
              Search
            </Button>
          </div>
          {searchResult && (
            <div className="space-y-3">
              <p className="text-sm text-neutral-500">
                {searchResult.semantic_result_count} semantic,{' '}
                {searchResult.bm25_result_count} BM25,{' '}
                {searchResult.image_result_count} image result(s).
              </p>
              {searchResult.semantic_text_results.slice(0, 3).map((result) => (
                <div
                  key={result.chunk_id}
                  className="rounded border border-neutral-200 p-3 text-sm dark:border-neutral-800"
                >
                  <div className="font-medium">{result.heading || 'Chunk'}</div>
                  <p className="mt-1 line-clamp-3 text-neutral-600 dark:text-neutral-400">
                    {result.content_text}
                  </p>
                </div>
              ))}
              {searchResult.image_results.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {searchResult.image_results.slice(0, 4).map((result) => (
                    <div
                      key={`${result.revision_id}-${result.page_number}-${result.rank}`}
                      className="overflow-hidden rounded border border-neutral-200 text-sm dark:border-neutral-800"
                    >
                      <img
                        src={ragPageArtifactUrl(
                          result.revision_id,
                          result.page_number,
                          'preview',
                        )}
                        alt={`Page ${result.page_number} preview`}
                        className="aspect-[4/3] w-full bg-neutral-100 object-contain dark:bg-neutral-900"
                      />
                      <div className="space-y-1 p-3">
                        <div className="font-medium">
                          Page {result.page_number}
                        </div>
                        <p className="line-clamp-2 text-xs text-neutral-500">
                          {result.caption || 'Image candidate'}
                        </p>
                        <a
                          className="text-xs text-blue-600 hover:underline"
                          href={ragPageArtifactUrl(
                            result.revision_id,
                            result.page_number,
                            'image',
                          )}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open page image
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function StepBadge({
  label,
  isActive,
  isDone,
  isError,
}: {
  label: string
  isActive?: boolean
  isDone?: boolean
  isError?: boolean
}) {
  return (
    <div
      className={`rounded-full border px-3 py-2 text-sm ${
        isError
          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300'
          : isActive
            ? 'border-neutral-950 bg-neutral-950 text-white dark:border-neutral-50 dark:bg-neutral-50 dark:text-neutral-950'
            : isDone
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/40 dark:text-green-300'
              : 'border-neutral-200 text-neutral-500 dark:border-neutral-800'
      }`}
    >
      <span className="inline-flex items-center gap-2">
        {isError ? (
          <AlertCircle className="h-4 w-4" />
        ) : isDone && !isActive ? (
          <CheckCircle2 className="h-4 w-4" />
        ) : null}
        {label}
      </span>
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-right font-medium text-neutral-950 dark:text-neutral-50">
        {value}
      </dd>
    </div>
  )
}

function getField(source: unknown, key: string) {
  if (!source || typeof source !== 'object') return undefined
  return (source as Record<string, unknown>)[key]
}

function getMetadata(source: unknown) {
  return (
    (getField(source, 'metadata_json') as
      | Record<string, unknown>
      | undefined) ||
    (getField(source, 'metadataJson') as Record<string, unknown> | undefined) ||
    {}
  )
}

function getSuggestedSkipPages(
  metadata: Record<string, unknown>,
  candidates: WorkflowData['structureReview'],
) {
  if (typeof metadata.suggested_skip_pages === 'string') {
    return metadata.suggested_skip_pages
  }
  const candidatePages = candidates
    .filter((candidate) => {
      const predicted = String(candidate.predicted_class || '').toLowerCase()
      return predicted.includes('skip') || predicted.includes('front')
    })
    .map((candidate) => candidate.page_number)
  return formatPageRanges(candidatePages)
}

function hasPageReviewResult(
  metadata: Record<string, unknown>,
  candidates: WorkflowData['structureReview'],
) {
  return (
    Object.prototype.hasOwnProperty.call(metadata, 'suggested_skip_pages') ||
    typeof metadata.suggested_skip_pages_reviewed_at === 'string' ||
    (metadata.suggested_skip_pages_review !== null &&
      typeof metadata.suggested_skip_pages_review === 'object') ||
    candidates.length > 0
  )
}

function wasPageReviewRequested(metadata: Record<string, unknown>) {
  const uploadMetadata = getField(metadata, 'metadata_json')
  return (
    getField(uploadMetadata, 'skip_page_review_requested') === true ||
    getField(metadata, 'skip_page_review_requested') === true
  )
}

function getLatestJob(status: WorkflowData['status']) {
  return (
    (getField(status, 'latest_job') as Record<string, unknown> | null) || null
  )
}

function getProgressCounts(workflow: WorkflowData) {
  const latestFinalize = [...workflow.events]
    .reverse()
    .map((event) => getField(event, 'data') || getField(event, 'event_data'))
    .find((data) => data && typeof data === 'object') as
    | Record<string, unknown>
    | undefined
  return {
    pages: workflow.pages.length,
    skippedPages: workflow.pages.filter((page) => page.skipped).length,
    chunks: workflow.chunks.length,
    textEmbeddings:
      Number(latestFinalize?.text_embedding_count) || workflow.chunks.length,
    imageEmbeddings:
      Number(latestFinalize?.image_embedding_count) ||
      workflow.pages.filter((page) => !page.skipped).length,
  }
}

function getReadinessIssues(
  counts: ReturnType<typeof getProgressCounts>,
  isComplete: boolean,
) {
  if (!isComplete) return []
  const nonSkippedPages = counts.pages - counts.skippedPages
  const issues: Array<string> = []
  if (nonSkippedPages > 0 && counts.imageEmbeddings !== nonSkippedPages) {
    issues.push(
      `Image embeddings (${counts.imageEmbeddings}) do not match non-skipped pages (${nonSkippedPages}).`,
    )
  }
  if (counts.chunks > 0 && counts.textEmbeddings !== counts.chunks) {
    issues.push(
      `Text embeddings (${counts.textEmbeddings}) do not match chunks (${counts.chunks}).`,
    )
  }
  if (nonSkippedPages > 0 && counts.chunks === 0) {
    issues.push('No chunks were found for the non-skipped pages.')
  }
  return issues
}

function getPageReviewSummary(
  events: Array<Record<string, unknown>>,
  counts: ReturnType<typeof getProgressCounts>,
  needsPageReview: boolean,
  revisionStatus: string,
  latestJob: Record<string, unknown> | null,
): {
  status: string
  variant: WorkflowRunState['variant']
  description: string
  details?: string
} {
  if (needsPageReview) {
    return {
      status: 'Action required',
      variant: 'notice',
      description:
        'Confirm front matter, contents, indexes, appendices, or blank pages before ingestion starts.',
    }
  }

  if (revisionStatus === 'draft' || (!latestJob && events.length === 0)) {
    return {
      status: 'Not started',
      variant: 'neutral',
      description:
        'Page review will appear here once the upload has been scanned.',
    }
  }

  const skipFinalized = events.some((event) => {
    const stage = String(getField(event, 'stage') || '')
    const message = String(getField(event, 'message') || '').toLowerCase()
    return stage === 'page_skip_finalize' && message.includes('finalized')
  })
  const skippedDetail =
    counts.pages > 0
      ? `${counts.skippedPages} skipped page${
          counts.skippedPages === 1 ? '' : 's'
        } across ${counts.pages} total page${counts.pages === 1 ? '' : 's'}.`
      : undefined

  if (skipFinalized || counts.pages > 0) {
    return {
      status: 'Complete',
      variant: 'positive',
      description:
        'Skip-page review is saved. The ingestion worker is using these rules for the document.',
      details: skippedDetail,
    }
  }

  return {
    status: 'Submitted',
    variant: 'informative',
    description:
      'Page review has been submitted and is waiting for the worker to finalize skip rules.',
  }
}

function getStageLabel(stage: string) {
  return stage ? stage.replace(/_/g, ' ') : 'not started'
}

function isProblemMessage(message: string) {
  const normalized = message.toLowerCase()
  return (
    normalized.includes('failed') ||
    normalized.includes('error') ||
    normalized.includes('could not') ||
    normalized.includes('unavailable') ||
    normalized.includes('timed out') ||
    normalized.includes('cannot ')
  )
}

function isQueueProblemMessage(message: string) {
  const normalized = message.toLowerCase()
  return (
    normalized.includes('worker queue could not be reached') ||
    normalized.includes('queue could not be reached') ||
    normalized.includes('queue unavailable') ||
    normalized.includes('pgqueuer')
  )
}

function getEventSeverity(event: Record<string, unknown>) {
  const message = String(getField(event, 'message') || '')
  if (isProblemMessage(message)) return 'negative'
  const stage = String(getField(event, 'stage') || '').toLowerCase()
  if (stage.includes('review')) return 'notice'
  return 'neutral'
}

function getWorkflowRunState(
  latestJob: Record<string, unknown> | null,
  events: Array<Record<string, unknown>>,
  revisionStatus: string,
  pageReviewState: {
    pageReviewReady?: boolean
    pageReviewScanPending?: boolean
  } = {},
): WorkflowRunState {
  const latestJobStatus = String(
    getField(latestJob, 'status') || (latestJob ? 'queued' : ''),
  )
  const currentStage = String(getField(latestJob, 'current_stage') || '')
  const latestError = getLatestError(latestJob, events)

  if (revisionStatus === 'failed' || latestJobStatus === 'failed') {
    return {
      kind: 'failed',
      label: 'Ingestion failed',
      summary:
        latestError ||
        `The ingest job failed at ${getStageLabel(currentStage)}. Review the event timeline and retry when the underlying issue is fixed.`,
      variant: 'negative',
    }
  }

  if (revisionStatus === 'completed' || latestJobStatus === 'completed') {
    return {
      kind: 'completed',
      label: 'Ingestion complete',
      summary: 'This revision is indexed and ready for verification search.',
      variant: 'positive',
    }
  }

  if (!latestJob && pageReviewState.pageReviewReady) {
    return {
      kind: 'awaiting_review',
      label: 'Page review ready',
      summary:
        'The upload scan is complete. Confirm the suggested pages to skip before starting ingestion.',
      variant: 'notice',
    }
  }

  if (!latestJob && pageReviewState.pageReviewScanPending) {
    return {
      kind: 'queued',
      label: 'Scanning upload',
      summary:
        'The document is being checked for front matter, indexes, appendices, and blank pages.',
      variant: 'informative',
    }
  }

  if (latestJobStatus === 'queued' && isQueueProblemMessage(latestError)) {
    return {
      kind: 'blocked',
      label: 'Ingestion blocked',
      summary: `The job is queued, but the worker queue reported a problem: ${latestError}. Fix or redeploy the RAG worker, then refresh this page; the queued job should resume automatically.`,
      variant: 'negative',
    }
  }

  if (latestJobStatus === 'queued') {
    return {
      kind: 'queued',
      label: 'Queued',
      summary: `The job is waiting for the RAG worker to pick it up at ${getStageLabel(currentStage)}.`,
      variant: 'informative',
    }
  }

  if (latestJobStatus === 'running') {
    return {
      kind: 'running',
      label: 'Ingestion running',
      summary: `The worker is processing ${getStageLabel(currentStage)}.`,
      variant: 'informative',
    }
  }

  if (latestJobStatus === 'awaiting_review') {
    return {
      kind: 'awaiting_review',
      label: 'Review required',
      summary: `The workflow is paused for admin review at ${getStageLabel(currentStage)}.`,
      variant: 'notice',
    }
  }

  return {
    kind: 'not_started',
    label: 'Not started',
    summary: 'No ingest job is currently running for this revision.',
    variant: 'neutral',
  }
}

function getStageProgressValue(stage: string) {
  const index = STAGES.indexOf(stage)
  if (index < 0) return 0
  return ((index + 1) / STAGES.length) * 100
}

function getLatestError(
  latestJob: Record<string, unknown> | null,
  events: Array<Record<string, unknown>>,
) {
  const jobError =
    getField(latestJob, 'last_error') || getField(latestJob, 'error')
  if (jobError) return String(jobError)
  const failedEvent = [...events].reverse().find((event) => {
    const message = String(getField(event, 'message') || '').toLowerCase()
    return isProblemMessage(message)
  })
  return failedEvent ? String(getField(failedEvent, 'message')) : ''
}

function getCandidateUnitIds(candidate: WorkflowData['chunkReview'][number]) {
  const ids = new Set<string>()
  for (const id of candidate.review_unit_ids || []) ids.add(id)
  for (const unit of candidate.units_json || []) {
    const unitId =
      getField(unit, 'id') ||
      getField(unit, 'unit_id') ||
      getField(unit, 'source_unit_id')
    if (unitId) ids.add(String(unitId))
  }
  return [...ids]
}

function findCandidateUnit(
  candidate: WorkflowData['chunkReview'][number],
  unitId: string,
) {
  return (candidate.units_json || []).find((unit) => {
    const candidateId =
      getField(unit, 'id') ||
      getField(unit, 'unit_id') ||
      getField(unit, 'source_unit_id')
    return String(candidateId ?? '') === unitId
  })
}

function getUnitLabel(
  candidate: WorkflowData['chunkReview'][number],
  unitId: string,
) {
  const unit = findCandidateUnit(candidate, unitId)
  const page = getField(unit, 'page_number') || getField(unit, 'page')
  const kind = getField(unit, 'kind') || getField(unit, 'type')
  return [kind ? String(kind) : 'Unit', page ? `page ${page}` : unitId]
    .filter(Boolean)
    .join(' · ')
}

function getUnitPreview(
  candidate: WorkflowData['chunkReview'][number],
  unitId: string,
) {
  const unit = findCandidateUnit(candidate, unitId)
  return String(
    getField(unit, 'text') ||
      getField(unit, 'content') ||
      getField(unit, 'plain_text') ||
      getField(unit, 'preview_text') ||
      '',
  )
}

function ragPageArtifactUrl(
  revisionId: string | null | undefined,
  pageNumber: number | null | undefined,
  variant: 'image' | 'preview',
) {
  return revisionId && pageNumber
    ? `/api/artifacts/${encodeURIComponent(
        `rag-page.${revisionId}.${pageNumber}.${variant}`,
      )}`
    : ''
}

function formatPageRanges(pages: Array<number>) {
  const sorted = [...new Set(pages)]
    .filter((page) => Number.isInteger(page) && page > 0)
    .sort((a, b) => a - b)
  const ranges: Array<string> = []
  for (let index = 0; index < sorted.length; index++) {
    const start = sorted[index]
    let end = start
    while (sorted[index + 1] === end + 1) {
      end = sorted[index + 1]
      index += 1
    }
    ranges.push(start === end ? String(start) : `${start}-${end}`)
  }
  return ranges.join(',')
}

export const __adminRagWorkflowTestUtils = {
  formatPageRanges,
  getCandidateUnitIds,
  getField,
  getLatestError,
  getLatestJob,
  getMetadata,
  hasPageReviewResult,
  getEventSeverity,
  getPageReviewSummary,
  getProgressCounts,
  getReadinessIssues,
  getStageProgressValue,
  getWorkflowRunState,
  getSuggestedSkipPages,
  getUnitLabel,
  getUnitPreview,
  ragPageArtifactUrl,
  wasPageReviewRequested,
}
